import { useEffect, useState } from "react";
import { X, Download, Printer, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { ShareWhatsAppButton } from "./ShareWhatsAppButton";

/**
 * Reusable PDF Preview Modal.
 * Props:
 *   open: bool
 *   onClose: fn
 *   title: string (contoh: "Nota Kasbon KAS-2026-0001")
 *   url: path relative ke api.defaults.baseURL, contoh "/nota/kasbon/{id}/pdf"
 *   filename: nama file saat download, contoh "Nota_Kasbon_KAS-2026-0001.pdf"
 *   method: "GET" (default) atau "POST" untuk preview draft
 *   body: payload JSON kalau method POST
 *   draft: bool — kalau true, sembunyikan tombol Download
 *   share: optional object {jenis, id, vendor_no_hp, vendor_nama, nota_kode} — kalau diisi, tombol WA tampil
 */
export function PreviewNotaModal({ open, onClose, title, url, filename, method = "GET", body = null, draft = false, share = null }) {
    const [blobUrl, setBlobUrl] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open || !url) return;
        setLoading(true);
        setErr("");
        setBlobUrl("");
        let revoked = null;
        (async () => {
            try {
                const token = localStorage.getItem("bvg_token");
                const opts = {
                    method,
                    headers: { Authorization: `Bearer ${token}` },
                };
                if (method === "POST" && body != null) {
                    opts.headers["Content-Type"] = "application/json";
                    opts.body = JSON.stringify(body);
                }
                const r = await fetch(`${api.defaults.baseURL}${url}`, opts);
                if (!r.ok) {
                    const t = await r.text().catch(() => "");
                    throw new Error(t || `Gagal memuat PDF (${r.status})`);
                }
                const blob = await r.blob();
                const b = URL.createObjectURL(blob);
                revoked = b;
                setBlobUrl(b);
            } catch (e) {
                setErr(e.message || "Gagal memuat PDF");
            } finally {
                setLoading(false);
            }
        })();
        return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [open, url, method, body]);

    const handleDownload = () => {
        if (!blobUrl) return;
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || "nota.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handlePrint = () => {
        if (!blobUrl) {
            toast.error("PDF belum siap");
            return;
        }
        const w = window.open(blobUrl, "_blank");
        if (!w) {
            toast.error("Popup diblokir. Izinkan popup atau gunakan Download.");
            return;
        }
        // Beri waktu PDF render lalu panggil print
        setTimeout(() => { try { w.focus(); w.print(); } catch { } }, 800);
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-[60] flex md:items-center md:justify-center md:p-4"
             onClick={onClose} data-testid="preview-nota-modal">
            <div className="bg-white w-full h-full md:h-[85vh] md:w-[90vw] md:max-w-5xl flex flex-col md:rounded-2xl md:shadow-2xl md:border md:border-[var(--border)] overflow-hidden"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 flex-shrink-0">
                    <button onClick={onClose} className="order-1 md:order-2 text-slate-500 hover:text-slate-900 rounded-full p-1.5 hover:bg-slate-100"
                            data-testid="preview-nota-close-x" aria-label="Tutup">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="order-2 md:order-1 flex-1 min-w-0">
                        <h2 className="font-bold text-slate-900 text-sm truncate" data-testid="preview-nota-title">
                            Preview — {title}
                        </h2>
                        <p className="text-[11px] text-slate-500 hidden md:block">Pratinjau PDF sebelum dicetak atau diunduh</p>
                    </div>
                </div>

                <div className="flex-1 bg-slate-100 overflow-hidden min-h-0">
                    {loading && (
                        <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                            Memuat PDF...
                        </div>
                    )}
                    {err && !loading && (
                        <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
                            <AlertTriangle className="w-10 h-10 text-amber-500" />
                            <div className="text-slate-800 font-medium">Gagal memuat preview</div>
                            <div className="text-xs text-slate-500 max-w-md break-words">{err}</div>
                            <div className="text-xs text-slate-500 mt-2">
                                Browser Anda mungkin tidak mendukung preview PDF. Silakan download atau buka di tab baru.
                            </div>
                        </div>
                    )}
                    {!loading && !err && blobUrl && (
                        <iframe
                            title="pdf-preview"
                            src={blobUrl}
                            className="w-full h-full border-0"
                            data-testid="preview-nota-iframe"
                        />
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                    <button onClick={onClose} data-testid="preview-nota-tutup"
                            className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg font-medium text-sm">
                        Tutup
                    </button>
                    {!draft && share && share.jenis && share.id && (
                        <ShareWhatsAppButton
                            jenis={share.jenis}
                            id={share.id}
                            vendor_no_hp={share.vendor_no_hp}
                            vendor_nama={share.vendor_nama}
                            nota_kode={share.nota_kode}
                            testid="preview-nota-wa"
                        />
                    )}
                    <button onClick={handleDownload} disabled={!blobUrl}
                            data-testid="preview-nota-download"
                            className="px-4 py-2 border border-teal-600 text-teal-700 hover:bg-teal-50 rounded-lg font-medium text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Download className="w-4 h-4" /> Download
                    </button>
                    <button onClick={handlePrint} disabled={!blobUrl}
                            data-testid="preview-nota-print"
                            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Printer className="w-4 h-4" /> Cetak
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PreviewNotaModal;
